import React, { createContext, useContext, useState, useEffect } from 'react';
import { ColorTheme, applyTheme } from '../services/themeGenerator';

interface ThemeContextType {
  theme: ColorTheme | null;
  setTheme: (theme: ColorTheme) => void;
  resetTheme: () => void;
}

const defaultTheme: ColorTheme = {
  primary: '#6366f1',
  secondary: '#4f46e5',
  accent: '#818cf8',
  background: '#0f172a',
  surface: '#1e293b',
  text: '#f1f5f9',
  textSecondary: '#94a3b8',
  
  // Semantic colors
  error: '#ef4444',
  success: '#10b981',
  warning: '#f59e0b',
  highlight: '#818cf8',
  
  // UI colors
  border: '#334155',
  surfaceHover: '#334155',
  
  conceptColors: {
    theory: '#9333EA',
    canon: '#3B82F6',
    ship: '#EC4899',
    controversy: '#DC2626',
    meme: '#10B981',
    character: '#F59E0B'
  }
};

const blueTheme: ColorTheme = {
  primary: '#3855E8',
  secondary: '#0D1321',
  accent: '#7D7D7D',
  background: '#0D1321',
  surface: '#1a2332',
  text: '#FFEDDF',
  textSecondary: '#cccccc',
  
  // Semantic colors
  error: '#ff6b6b',
  success: '#51cf66',
  warning: '#ffd43b',
  highlight: '#74c0fc',
  
  // UI colors
  border: '#2d3748',
  surfaceHover: '#2d3748',
  
  conceptColors: {
    theory: '#9333EA',
    canon: '#3B82F6',
    ship: '#EC4899',
    controversy: '#DC2626',
    meme: '#10B981',
    character: '#F59E0B'
  }
};

const pinkTheme: ColorTheme = {
  primary: '#A3304F',
  secondary: '#946E83',
  accent: '#CDD5D1',
  background: '#DDF6E8',
  surface: '#ffffff',
  text: '#2d2d2d',
  textSecondary: '#666666',
  
  // Semantic colors
  error: '#dc2626',
  success: '#059669',
  warning: '#d97706',
  highlight: '#ec4899',
  
  // UI colors
  border: '#e5e7eb',
  surfaceHover: '#f9fafb',
  
  conceptColors: {
    theory: '#9333EA',
    canon: '#3B82F6',
    ship: '#EC4899',
    controversy: '#DC2626',
    meme: '#10B981',
    character: '#F59E0B'
  }
};

const orangeTheme: ColorTheme = {
  primary: '#FFA400',
  secondary: '#FD6500',
  accent: '#2A2A72',
  background: '#232328',
  surface: '#2d2d32',
  text: '#ffffff',
  textSecondary: '#cccccc',
  
  // Semantic colors
  error: '#f87171',
  success: '#4ade80',
  warning: '#fbbf24',
  highlight: '#f97316',
  
  // UI colors
  border: '#404040',
  surfaceHover: '#3a3a3f',
  
  conceptColors: {
    theory: '#9333EA',
    canon: '#3B82F6',
    ship: '#EC4899',
    controversy: '#DC2626',
    meme: '#10B981',
    character: '#F59E0B'
  }
};

export const presetThemes = {
  default: defaultTheme,
  blue: blueTheme,
  pink: pinkTheme,
  orange: orangeTheme
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<ColorTheme>(orangeTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const resetTheme = () => {
    setTheme(orangeTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, resetTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}